import * as d3 from "d3";
import { startOfWeek } from "date-fns";
import moment from "moment";
import { RefObject, useEffect, useRef, useState } from "react";
import { InteractiveStatMap } from "../../components/plan/StatMap/interactiveStatMap";
import { StatToolTip } from "../../components/plan/InteractiveMap/toolTip";
import IFloor from "../../models/floor";
import ILocal from "../../models/local";
import { IHBScanFloor, IProcessedScan, IRoom, StatSummary } from "../../models/stat";
import BuildingService from "../../services/building/BuildingService";
import HBScanStatService from "../../services/stat/HBScanStatService";
import DeepSet from "../../utils/deepSet";
import objectUtils from "../../utils/objectUtils";
import {
    ArrowFunction,
    AsyncArrowFunction,
    StateAction,
} from "../../utils/typeAlias";
import UniqueKey from "../../utils/uniqueKey";
import { getRoomMinutes } from "./hbscanPage";
import IEmployee from "../../models/employee";
import { ICircleCreationEvent } from "../../components/plan/StatMap/edit/circle";
import CleaningScheduleService from "../../services/local/CleaningScheduleService";
import ICleaningSchedule, {
    dayNames,
} from "../../models/cleaningSchedule";
import { canEdit } from "../../utils/permissions";
import IAdminPageBuildingDetail from "../../models/adminPage/adminPageBuildingDetail";
import { IRoute } from "../../models/route";
import routeService from "../../services/routeService/RouteService";

const getLocalIdsFromMap = (id: string) => {
    let svgSelection = d3.select<SVGElement, any>(`svg#${id}`);

    const localIds: number[] = [];
    svgSelection.selectAll("circle").each(function () {
        const circle = d3.select(this);
        if (circle.empty()) return;
        const id = circle.attr("id");
        if (!id) return;

        localIds.push(Number(id.replace("cp", "")));
    });
    return localIds;
};

const initMap = async (
    ref: RefObject<HTMLDivElement>,
    mapUrl: string
): Promise<[string, number[]]> => {
    const xml = await d3.xml(mapUrl);
    const svg = xml.documentElement;
    ref.current?.replaceChildren(svg);
    const id = UniqueKey.generateId({ prefix: "map", length: 8 });
    svg.id = id;

    return [id, getLocalIdsFromMap(id)];
};

const createMap = (
    ref: RefObject<HTMLDivElement>,
    svgId: string,
    event: ICircleCreationEvent,
    floorId: number,
    setMapSaving: StateAction<number>,
    circleRadius: number
) => {
    let svg = d3.select<SVGElement, any>(`svg#${svgId}`);

    if (svg.empty()) return;

    let divToolTip = d3.select(ref.current).append("div");
    let tooltip = new StatToolTip(divToolTip, ref);

    const toolBoxButtonIds = ["#addButton", "#deleteButton", "#saveButton"];
    const toolBoxButtons: d3.Selection<
        HTMLButtonElement,
        any,
        HTMLElement,
        any
    >[] = [];
    toolBoxButtonIds.forEach((id) => {
        const button = d3.select<HTMLButtonElement, any>(id);
        toolBoxButtons.push(button);
    });

    return new InteractiveStatMap(
        svg,
        tooltip,
        event,
        floorId,
        setMapSaving,
        toolBoxButtons,
    );
};

const useMapScansFloors = () => {
    const ref = useRef<HTMLDivElement>(null);

    const [hbScanFloors, setHbscanFloors] = useState<IHBScanFloor[]>([]);
    const [filteredHBScanFloors, setFilteredHBScanFloors] = useState<
        IHBScanFloor[]
    >([]);

    const [start, setStart] = useState<Date>(startOfWeek(new Date()));
    const [end, setEnd] = useState<Date | undefined>(new Date());

    const [floors, setFloors] = useState<IFloor[]>([]);
    const [selectedFloors, setSelectedFloors] = useState<IFloor[]>([]);

    const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
    const [buildingId, setBuildingId] = useState<number>(-1);

    const [selectedRoutes, setSelectedRoutes] = useState<number[]>([]);
    const [routes, setRoutes] = useState<IRoute[]>([]);


    const [employees, setEmployees] = useState<IEmployee[]>([]);
    const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);

    const [selectedLocalId, setSelectedLocalId] = useState<number>();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isEditPopup, setIsEditPopup] = useState<boolean>(false);

    const [floorId, setFloorId] = useState(0);

    const [map, setMap] = useState<InteractiveStatMap>();
    const [mapSaving, setMapSaving] = useState<number>(0);

    const [circleRadius, setCricleRadius] = useState(InteractiveStatMap.DEFAULT_RADIUS);

    const onCricleRadiusChange = (radius: number) => {
        setCricleRadius(radius);
        map?.setCircleRadius(radius);
    }

    useEffect(() => {
        if (!map) return;
        setCricleRadius(map.getCircleRadius())
    }, [map])

    useEffect(() => {
        if (buildingId <= 0) return;
        routeService.GetLocalRoutesByBuildingId(buildingId).then(setRoutes);
    }, [buildingId])

    useEffect(() => {
        const getData = async (): Promise<[IFloor[], IAdminPageBuildingDetail[]]> => {
            const floors = await HBScanStatService.getFloors();
            const buildingIds = new DeepSet(floors.map((f) => f.buildingId)).values();
            const buildings = await BuildingService.GetBuildingsByIds(buildingIds);
            return [floors, buildings];
        };

        getData().then(([floors, buildings]) => {
            setFloors(floors);
            setBuildings(buildings);
            if (!buildings.find((b) => b.id === buildingId)) setBuildingId(buildings[0].id);
        }).catch((error) => { });
    }, [mapSaving]);

    useEffect(() => {
        setSelectedFloors(
            floors
                .filter((f) => f.buildingId === buildingId)
                .filter((f) => f.hbscanMapUrl)
                .sort((a, b) => a.floorNumber - b.floorNumber)
        );
    }, [floors, buildingId]);

    useEffect(() => {
        if (!end) return;

        if (!selectedFloors.length) {
            setFloorId(0);
            setIsLoading(false);
            return;
        }

        const getHbScanFloors: ArrowFunction<IHBScanFloor[]> = (
            locals: ILocal[],
            scans: IProcessedScan[],
            localCleaning: ICleaningSchedule[]
        ) => {
            const wholeStartDay = moment(start).startOf('day');
            const wholeEndDay = moment(end).endOf('day');
            const nDays = wholeEndDay.diff(wholeStartDay, "days") + 1;

            const localSchedules = new Map(
                localCleaning.map((lc) => [lc.localId, lc])
            );
            const days = objectUtils
                .range(nDays)
                .map((d) => wholeStartDay.clone().add(d, 'days').day());

            return selectedFloors.map((floor, i) => {
                const selectedLocals = locals.filter(
                    (local) => local.floorId === floor.id
                );
                const rooms: IRoom[] = selectedLocals.map((local) => {
                    const schedule = localSchedules.get(local.id);
                    const dayCleaningSum = days.reduce((acc, day) => {
                        const dayName = dayNames.get(day);
                        if (!dayName) return acc;

                        const dayCleaning = schedule?.[dayName];

                        return acc + (dayCleaning || 0);
                    }, 0);

                    return {
                        minMinutes: dayCleaningSum,
                        roomId: local.id,
                        roomName: local.name,
                        scans: scans.filter((s) => s.start.localId === local.id),
                        isLocalLocked: local.isLocked,
                    };
                });

                return {
                    ...floor,
                    mapUrl: floor.hbscanMapUrl,
                    index: i,
                    rooms,
                };
            });
        };

        const getHbscanData: AsyncArrowFunction<
            [ILocal[], IProcessedScan[], ICleaningSchedule[]]
        > = async () => {
            const urls = selectedFloors?.map((f) => f.hbscanMapUrl);
            const mapIds = await Promise.all(
                urls.map((url) => initMap(ref, url))
            );
            const allLocalIds = mapIds.flatMap(([_, ids]) => ids);
            const localIds = selectedRoutes.length === 0
                ? allLocalIds
                : (() => {
                    const routeIdSet = new Set(routes.filter(r => selectedRoutes.includes(r.id)).flatMap(route => route.locals.map(l => l.id)));
                    return allLocalIds.filter(id => routeIdSet.has(id));
                })();

            const locals = await HBScanStatService.getLocals(localIds);
            let scans = await HBScanStatService.getMapScansByBuilding(localIds, start, end);
            if (scans.length < 1)
                scans = await HBScanStatService.getInOutScansByBuildingId(
                    buildingId,
                    start,
                    end
                );

            const localsCleaning =
                await CleaningScheduleService.GetCleaningSchedules(localIds);
            return [locals || [], scans || [], localsCleaning];
        };

        const getEmployeesFromScans: ArrowFunction<IEmployee[]> = (
            scans: IProcessedScan[]
        ) => {
            return new DeepSet(
                scans.map((s) => ({ id: s.start.userId, name: s.start.username }))
            ).values();
        };
        setIsLoading(true);
        getHbscanData()
            .then(([locals, scans, localsCleaning]) => {
                setEmployees(getEmployeesFromScans(scans));
                return getHbScanFloors(locals, scans, localsCleaning);
            })
            .then((hbscanFloors) => {
                setHbscanFloors(hbscanFloors);
                setFilteredHBScanFloors([]);
                setIsLoading(false);
            });
    }, [selectedFloors, start, end, selectedRoutes]);

    useEffect(() => {
        if (!hbScanFloors.find((f) => f.id === floorId)) setFloorId(hbScanFloors.length > 0 ? hbScanFloors[0].id : 0);
        setFilteredHBScanFloors(objectUtils.clone(hbScanFloors));
        setSelectedEmployees([]);
    }, [hbScanFloors]);

    useEffect(() => {
        const setMapData = (floor: IHBScanFloor | undefined) => {
            if (!floor) return;

            const stats: Map<number, StatSummary> = floor.rooms.reduce(
                (acc, room) =>
                    acc.set(room.roomId, {
                        ...room,
                        minutes: getRoomMinutes(room),
                    }),
                new Map()
            );
            initMap(ref, floor.mapUrl).then(([mapId, _]) => {
                const event: ICircleCreationEvent = {
                    openPopup: () => setIsEditPopup(true),
                };
                const interactiveMap = createMap(
                    ref,
                    mapId,
                    event,
                    floor.id,
                    setMapSaving,
                    circleRadius
                );
                interactiveMap?.init(
                    stats,
                    setSelectedLocalId,
                    floor.isOvertimeCalculation,
                    canEdit()
                );

                setMap(interactiveMap);
            });
        };

        async function getFilteredFloors() {
            return new Promise<IHBScanFloor[]>((resolve) => {
                if (!selectedEmployees.length) resolve(hbScanFloors);

                const floors: IHBScanFloor[] = [];
                hbScanFloors.forEach((floor) => {
                    const rooms: IRoom[] = [];
                    floor.rooms.forEach((room) => {
                        const scans = room.scans.filter((s) =>
                            selectedEmployees.includes(s.start.userId)
                        );
                        rooms.push({ ...room, scans });
                    });
                    floors.push({ ...floor, rooms });
                });

                resolve(floors);
            });
        }

        getFilteredFloors().then((floors) => {
            setFilteredHBScanFloors(floors);
            setMapData(floors.find((f) => f.id === floorId));
        });
    }, [hbScanFloors, floorId, selectedEmployees]);

    return {
        hbScanFloors,
        filteredHBScanFloors,
        buildingId,
        setBuildingId,
        start,
        end,
        floorId,
        setFloorId,
        isLoading,
        buildings,
        employees,
        selectedEmployees,
        setSelectedEmployees,
        setStart,
        setEnd,
        selectedLocalId,
        setSelectedLocalId,
        setSelectedRoutes,
        selectedRoutes,
        routes,
        isEditPopup,
        setIsEditPopup,
        map,
        ref,
        circleRadius,
        onCricleRadiusChange
    };
};

export default useMapScansFloors;
